import { Head, Link } from "@inertiajs/react";
import {
  Box,
  Divider,
  Grid,
  Stack,
  Group,
  Button,
  Image,
  useMantineTheme,
} from "@mantine/core";
import PageShell from "../Components/Layout/PageShell";
import {
  Heading1,
  Heading2,
  Heading3,
  DimmedText,
  Lead,
  BodyText,
} from "../Components/UI/Typography";

// ── Data ───────────────────────────────────────────────────────────────────

interface Discipline {
  numeral: string;
  label: string;
  heading: string;
  /** Token name from theme.other for the accent color (decorative). */
  accentColor: string;
  /** Token name for the accessible text variant (AA-compliant). */
  accentTextColor: string;
  paragraphs: string[];
  /** Optional CTA button after the body. Currently only used on Think. */
  cta?: { label: string; href: string };
}

const disciplines: Discipline[] = [
  {
    numeral: "I",
    label: "TECH",
    heading: "Solo Founder + Full-Stack Developer",
    accentColor: "pillarTech",
    accentTextColor: "pillarTech",
    paragraphs: [
      "Rails + React + n8n + applied AI. Two sides of the same practice: my own products (VidHive, Learntokiz, WhereCanWeDance) and independent client engineering — mid-to-senior full-stack plus applied AI, delivered solo.",
      "Recent client work: nine months at Universal Tech Movement (38 Notion databases, 50+ automations, Salesforce sync, daily AI briefings) and five months at CDL Legal (Next.js migration, Strapi CMS, and a LangGraph multi-agent AI ticket engine validated at ~98% accuracy on 208 of 212 real tickets). Currently on retainer with CDL Legal and shipping the three products.",
      "Claude Code is my primary pair — every codebase I ship, mine and clients', is written that way. When off-the-shelf models can't solve a problem, I build the pipeline. AI is leverage, not a shortcut. Taste, system design, and judgment still have to be right.",
    ],
  },
  {
    numeral: "II",
    label: "DANCE",
    heading: "Urban Kiz Educator + Community Builder",
    accentColor: "pillarDance",
    accentTextColor: "pillarDance",
    paragraphs: [
      "Ten years running the Urban Kiz and Kizomba scene in Austin. Co-founder of Kiz ATX and NeoKizomba. Weekly practicas at Esquina Tango with co-instructor Alina. Produce Kiz Summit Exchange — a 10-day international intensive funded by the AACME Elevate Program.",
      "KSX 2026 runs October 30 through November 9 in Austin. Ten days of Urban Kiz workshops, socials, and cultural programming.",
      "Teaching, community building, and event production are the same practice at different scales.",
    ],
  },
  {
    numeral: "III",
    label: "MEDIA",
    heading: "Photographer + Video Producer",
    accentColor: "pillarMedia",
    accentTextColor: "pillarMedia",
    paragraphs: [
      "Live event photography and video for Austin's dance, music, and creative scenes. Motion graphics and music visualization on the video side.",
      "The camera is another interface into the same work: pay attention, catch the moment, ship the edit.",
    ],
  },
  {
    numeral: "IV",
    label: "THINK",
    heading: "Essays on Culture, Systems, and Self",
    accentColor: "pillarThink",
    accentTextColor: "pillarThinkAccessible",
    paragraphs: [
      "The writing side of the practice. Long-form essays on race and Black identity, the exploitation of Black creative labor, intentional relationships, and what community organizing reveals about human systems.",
      "Stream-of-consciousness that lands somewhere analytical.",
    ],
    cta: { label: "Read the essays →", href: "/think" },
  },
];

// ── Page ───────────────────────────────────────────────────────────────────

export default function About() {
  const heroPhoto =
    import.meta.env.VITE_HERO_PHOTO_URL || "/images/charles-hero.png";

  return (
    <>
      <Head>
        <title>About | Ogarocious</title>
        <meta
          name="description"
          content="Charles Ogar — solo founder and builder in Austin. I build software (my own products + independent client work) and produce the things around it: dance events, photography, community, essays."
        />
        <meta property="og:title" content="About | Ogarocious" />
        <meta
          property="og:description"
          content="Solo founder and builder — software, dance events, photography, community."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content={`${import.meta.env.VITE_SITE_URL || ""}/about`}
        />
        <meta
          property="og:image"
          content={import.meta.env.VITE_OG_IMAGE_URL || ""}
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About | Ogarocious" />
        <meta
          name="twitter:description"
          content="Solo founder and builder — software, dance events, photography, community."
        />
        <meta
          name="twitter:image"
          content={import.meta.env.VITE_OG_IMAGE_URL || ""}
        />
      </Head>

      <PageShell variant="hero" containerSize="lg">
        {/* Outer page rhythm — really generous section-to-section air.
            Uses plain flex + clamp() because Mantine 8's Stack gap prop
            silently ignores responsive objects (typed as MantineSpacing
            = single value only; casting to number is accepted by TS but
            fails at runtime, falling back to the 16px default). */}
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "clamp(48px, 5vw, 80px)",
          }}
        >

          {/* ── Hero ── */}
          <Grid gutter="xl" align="center">
            <Grid.Col span={{ base: 12, md: 7 }} order={{ base: 2, md: 1 }}>
              <Stack gap="xl">
                <Heading1 variant="shadow">I build things that work.</Heading1>

                <Lead>
                  Tech infrastructure, dance communities, and visual media.
                  Three disciplines that look different on the surface and run
                  on the same underlying logic: design a system, earn trust,
                  keep it running.
                </Lead>

                <Stack gap="sm">
                  <DimmedText>
                    I&apos;m a solo founder in Austin. I build software — my
                    own products (VidHive, Learntokiz, WhereCanWeDance) and
                    independent engineering work for organizations that need
                    it — and produce the things around it: dance events,
                    event photography, community platforms, and long-form
                    writing.
                  </DimmedText>
                  <DimmedText>
                    I ship this much solo by working with AI as a primary
                    collaborator — Claude Code for the software, custom
                    multi-agent pipelines when the problem calls for one.
                  </DimmedText>
                </Stack>
              </Stack>
            </Grid.Col>

            <Grid.Col span={{ base: 12, md: 5 }} order={{ base: 1, md: 2 }}>
              <Box
                style={{
                  position: "relative",
                  borderRadius: "var(--mantine-radius-md)",
                  overflow: "hidden",
                  maxWidth: 420,
                  marginInline: "auto",
                }}
              >
                <Image
                  src={heroPhoto}
                  alt="Charles Ogar"
                  radius="md"
                  style={{ width: "100%", height: "auto" }}
                />
              </Box>
            </Grid.Col>
          </Grid>

          {/* ── Four Disciplines ── generous inter-chapter air so
               each chapter reads as its own room, not a section in a
               list. Uses flex + clamp() (see outer Stack comment above:
               Mantine 8's responsive gap object silently no-ops). ── */}
          <Box
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "clamp(72px, 8vw, 120px)",
            }}
          >
            <Heading2 variant="shadow">Four disciplines.</Heading2>

            {disciplines.map((d) => (
              <ChapterBlock key={d.numeral} discipline={d} />
            ))}
          </Box>

          {/* ── The Thread (coda / thesis at the end, earned) ── */}
          <Box>
            <Stack gap="xl">
              <Heading2 variant="shadow">The thread</Heading2>
              <Box style={{ maxWidth: "65ch" }}>
                <Lead
                  style={{
                    fontStyle: "italic",
                    lineHeight: 1.55,
                    textWrap: "pretty",
                  }}
                >
                  My best work sits in the intersections. Dance taught me to
                  notice when a system breaks. Building software taught me
                  how to fix it. Nonprofit operations taught me that neither
                  matters if the humans in the loop don&apos;t trust each
                  other. The disciplines improve each other — that&apos;s
                  the practice.
                </Lead>
              </Box>
            </Stack>
          </Box>

          {/* ── Work with me (final CTA moment — earned after the essay) ── */}
          <Box>
            <Stack gap="lg">
              <Heading2 variant="shadow">Work with me</Heading2>
              <DimmedText size="lg" style={{ maxWidth: "65ch" }}>
                Client engineering, product builds, or a conversation about
                any of the above.
              </DimmedText>
              <Group gap="md" mt="md">
                <Button
                  variant="gradient"
                  size="lg"
                  component={Link}
                  href="/tech"
                >
                  See Projects
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  component={Link}
                  href="/contact"
                >
                  Get in Touch
                </Button>
              </Group>
            </Stack>
          </Box>

        </Box>
      </PageShell>
    </>
  );
}

// ── Chapter block ──────────────────────────────────────────────────────────

/**
 * Editorial chapter treatment for a single discipline.
 * Numeral + label + accent hairline + heading + body paragraphs.
 * No card chrome — the per-discipline accent color is the differentiator.
 */
function ChapterBlock({ discipline }: { discipline: Discipline }) {
  const theme = useMantineTheme();
  const other = theme.other as Record<string, string>;
  const accent = other[discipline.accentColor];
  const accentText = other[discipline.accentTextColor];

  return (
    <Box>
      <Stack gap="xl">

        {/* Chapter header: numeral over label + hairline rule in accent */}
        <Box>
          <Box
            style={{
              fontFamily: "var(--mantine-font-family-headings)",
              fontWeight: 800,
              fontSize: "clamp(2.5rem, 5vw, 4rem)",
              lineHeight: 1,
              color: accent,
              marginBottom: "0.75rem",
            }}
          >
            {discipline.numeral}
          </Box>
          <Group gap="md" align="center" wrap="nowrap">
            <Box
              style={{
                fontFamily: "var(--mantine-font-family-headings)",
                fontWeight: 700,
                fontSize: "0.875rem",
                letterSpacing: "0.18em",
                color: accentText,
              }}
            >
              {discipline.label}
            </Box>
            <Divider
              color={accent}
              size={1}
              style={{ flex: 1, opacity: 0.5 }}
            />
          </Group>
        </Box>

        {/* Discipline heading — larger than the pillar cards ever got */}
        <Heading3
          style={{
            margin: 0,
            maxWidth: "22ch",
            lineHeight: 1.2,
          }}
        >
          {discipline.heading}
        </Heading3>

        {/* Body — each paragraph is its own beat with generous leading */}
        <Stack gap="lg" style={{ maxWidth: "65ch" }}>
          {discipline.paragraphs.map((p, i) => (
            <BodyText key={i} style={{ lineHeight: 1.9, maxWidth: "none" }}>
              {p}
            </BodyText>
          ))}
        </Stack>

        {/* Optional CTA — currently only Think has one */}
        {discipline.cta && (
          <Button
            variant="subtle"
            color="gray"
            size="sm"
            component={Link}
            href={discipline.cta.href}
            style={{
              alignSelf: "flex-start",
              color: accentText,
            }}
          >
            {discipline.cta.label}
          </Button>
        )}

      </Stack>
    </Box>
  );
}
